/** GPU-first adjustment dispatch.
 *
 *  This module is the bridge between `lib/adjustments/dispatch.ts`
 *  (the CPU dispatch, which everything has always used) and the
 *  per-kind GPU shader implementations under `./adjustments/`. The
 *  CPU dispatch calls `applyAdjustmentOnGpu` first; if it returns
 *  `true`, the GPU pixels are already in the target canvas and the
 *  CPU path skips its work. If it returns `false` — whether because
 *  WebGL is unavailable, the kind has no GPU implementation yet, or
 *  a shader compile failed — the CPU dispatch runs as before. The
 *  user-facing behavior is correctness-equivalent either way; the
 *  GPU path is purely a perf accelerator.
 *
 *  Kind coverage is intentionally incremental. PR 1 shipped the
 *  LUT-based kinds (B/C, Curves, Levels, Posterize, Threshold).
 *  PR 2 added the HSL-space and direct-RGB kinds (Hue/Sat,
 *  Vibrance, Color Balance, Shadows/Highlights). PR 3 added the
 *  single-pass spatial filters (Vignette, Chromatic Aberration,
 *  Emboss, Find Edges). PR 4 added the UV-remap kinds (Twirl, Pinch,
 *  Lens Correction) with the runtime's `filter: 'linear'` mode.
 *  PR 5 added the first batch of convolution-based kinds (Gaussian
 *  Blur, Box Blur, Motion Blur) — Gaussian and Box use the
 *  runtime's multi-pass / ping-pong infrastructure
 *  (`applyShaderPasses`) for the separable 2-pass kernel trick;
 *  Motion Blur is single-pass along an arbitrary direction.
 *  PR 6 added the sharpen pair (Unsharp Mask, Smart Sharpen) and
 *  Radial Blur. Sharpen pair uses a 3-pass chain that reuses the
 *  Gaussian H/V shaders verbatim and adds a combine pass reading
 *  the original source via `u_original` (runtime extension).
 *  Smart Sharpen does the combine in BT.601 YCbCr space for halo-
 *  suppressed luma-only sharpening. Radial Blur is single-pass
 *  spin / zoom averaging — two shaders, one per mode.
 *  PR 7 added the stochastic pair (Add Noise, Reduce Noise) and
 *  bundled Y-axis fixes for Motion Blur + Emboss (same chirality
 *  bug Twirl had — see twirl.ts). Add Noise uses a Wang-style
 *  integer hash in GLSL ES 3.00's native uint type — bit-identical
 *  to the JS path. Reduce Noise is a single-pass bilateral filter.
 *  PR 8 closes out the GPU port with Median (per-channel selection
 *  sort up to the median rank, constant-bound 49-element local
 *  arrays) and Pixelate (two-pass downsample + nearest-neighbor
 *  upsample, uses the runtime's new `targetDims` for the small
 *  intermediate FBO). All adjustment kinds are now on GPU.
 *  Every kind without a case in the switch below transparently
 *  uses the JS path. */

import type { AdjustmentConfig } from '../../types';
import { applyAddNoiseOnGpu } from './adjustments/add-noise';
import { applyBoxBlurOnGpu } from './adjustments/box-blur';
import { applyBrightnessContrastOnGpu } from './adjustments/brightness-contrast';
import { applyChromaticAberrationOnGpu } from './adjustments/chromatic-aberration';
import { applyColorBalanceOnGpu } from './adjustments/color-balance';
import { applyCurvesOnGpu } from './adjustments/curves';
import { applyEmbossOnGpu } from './adjustments/emboss';
import { applyFindEdgesOnGpu } from './adjustments/find-edges';
import { applyGaussianBlurOnGpu } from './adjustments/gaussian-blur';
import { applyHueSaturationOnGpu } from './adjustments/hue-saturation';
import { applyLensCorrectionOnGpu } from './adjustments/lens-correction';
import { applyLevelsOnGpu } from './adjustments/levels';
import { applyMedianOnGpu } from './adjustments/median';
import { applyMotionBlurOnGpu } from './adjustments/motion-blur';
import { applyPinchOnGpu } from './adjustments/pinch';
import { applyPixelateOnGpu } from './adjustments/pixelate';
import { applyPosterizeOnGpu } from './adjustments/posterize';
import { applyRadialBlurOnGpu } from './adjustments/radial-blur';
import { applyReduceNoiseOnGpu } from './adjustments/reduce-noise';
import { applyShadowsHighlightsOnGpu } from './adjustments/shadows-highlights';
import { applySmartSharpenOnGpu } from './adjustments/smart-sharpen';
import { applyThresholdOnGpu } from './adjustments/threshold';
import { applyTwirlOnGpu } from './adjustments/twirl';
import { applyUnsharpMaskOnGpu } from './adjustments/unsharp-mask';
import { applyVibranceOnGpu } from './adjustments/vibrance';
import { applyVignetteOnGpu } from './adjustments/vignette';

/** Try the GPU dispatch for this adjustment kind. Returns `true`
 *  when the GPU pass succeeded and `target` is fully written;
 *  `false` to signal the caller should fall back to the CPU
 *  dispatch. Never throws — any GPU-internal failure surfaces as
 *  `false` to keep the fallback path predictable. */
export function applyAdjustmentOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  config: AdjustmentConfig,
): boolean {
  switch (config.kind) {
    case 'brightness-contrast':
      return applyBrightnessContrastOnGpu(source, target, config);
    case 'color-balance':
      return applyColorBalanceOnGpu(source, target, config);
    case 'curves':
      return applyCurvesOnGpu(source, target, config);
    case 'hue-saturation':
      return applyHueSaturationOnGpu(source, target, config);
    case 'levels':
      return applyLevelsOnGpu(source, target, config);
    case 'shadows-highlights':
      return applyShadowsHighlightsOnGpu(source, target, config);
    case 'vibrance':
      return applyVibranceOnGpu(source, target, config);
    case 'filter-posterize':
      return applyPosterizeOnGpu(source, target, config);
    case 'filter-threshold':
      return applyThresholdOnGpu(source, target, config);
    case 'filter-vignette':
      return applyVignetteOnGpu(source, target, config);
    case 'filter-chromatic-aberration':
      return applyChromaticAberrationOnGpu(source, target, config);
    case 'filter-emboss':
      return applyEmbossOnGpu(source, target, config);
    case 'filter-find-edges':
      return applyFindEdgesOnGpu(source, target, config);
    case 'filter-twirl':
      return applyTwirlOnGpu(source, target, config);
    case 'filter-pinch':
      return applyPinchOnGpu(source, target, config);
    case 'filter-lens-correction':
      return applyLensCorrectionOnGpu(source, target, config);
    case 'filter-gaussian-blur':
      return applyGaussianBlurOnGpu(source, target, config);
    case 'filter-box-blur':
      return applyBoxBlurOnGpu(source, target, config);
    case 'filter-motion-blur':
      return applyMotionBlurOnGpu(source, target, config);
    case 'filter-radial-blur':
      return applyRadialBlurOnGpu(source, target, config);
    case 'filter-unsharp-mask':
      return applyUnsharpMaskOnGpu(source, target, config);
    case 'filter-smart-sharpen':
      return applySmartSharpenOnGpu(source, target, config);
    case 'filter-add-noise':
      return applyAddNoiseOnGpu(source, target, config);
    case 'filter-reduce-noise':
      return applyReduceNoiseOnGpu(source, target, config);
    case 'filter-median':
      return applyMedianOnGpu(source, target, config);
    case 'filter-pixelate':
      return applyPixelateOnGpu(source, target, config);
    default:
      return false;
  }
}

/** True when the adjustment kind has a GPU implementation. Used by
 *  the CPU dispatch as a cheap pre-check before invoking the GPU
 *  path — no point spinning up a texture upload if we know the
 *  shader doesn't exist yet. Kept in lockstep with the switch in
 *  `applyAdjustmentOnGpu`. */
export function hasGpuImplementation(kind: AdjustmentConfig['kind']): boolean {
  switch (kind) {
    case 'brightness-contrast':
    case 'color-balance':
    case 'curves':
    case 'hue-saturation':
    case 'levels':
    case 'shadows-highlights':
    case 'vibrance':
    case 'filter-posterize':
    case 'filter-threshold':
    case 'filter-vignette':
    case 'filter-chromatic-aberration':
    case 'filter-emboss':
    case 'filter-find-edges':
    case 'filter-twirl':
    case 'filter-pinch':
    case 'filter-lens-correction':
    case 'filter-gaussian-blur':
    case 'filter-box-blur':
    case 'filter-motion-blur':
    case 'filter-radial-blur':
    case 'filter-unsharp-mask':
    case 'filter-smart-sharpen':
    case 'filter-add-noise':
    case 'filter-reduce-noise':
    case 'filter-median':
    case 'filter-pixelate':
      return true;
    default:
      return false;
  }
}
